
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    // Validate message length
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4hbn5cer93evkarroog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif mb-4 text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Send a Message
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            Reach out to discuss policy research, request briefings, or inquire about collaboration opportunities with congressional offices and federal agencies.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 border border-slate-200">
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Full Name <span className="text-red-800">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Email Address <span className="text-red-800">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm transition-all"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>
            </div>

            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-slate-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                Organization / Affiliation
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="Congressional Office, Federal Agency, Think Tank, etc."
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm transition-all"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                Subject
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-8 border border-slate-300 rounded-md focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm transition-all cursor-pointer"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <option value="">Select a topic</option>
                <option value="policy-briefing">Request Policy Briefing</option>
                <option value="research-collaboration">Research Collaboration</option>
                <option value="congressional-inquiry">Congressional Inquiry</option>
                <option value="media-request">Media Request</option>
                <option value="speaking-engagement">Speaking Engagement</option>
                <option value="general-inquiry">General Inquiry</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                Message <span className="text-red-800">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                placeholder="Please provide details about your inquiry..."
                className="w-full px-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm transition-all resize-none"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              <p className="text-xs text-slate-500 mt-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                {formData.message.length}/500 characters
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-checkbox-circle-line mr-2"></i>
                Thank you for your message. We will respond within 48 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-error-warning-line mr-2"></i>
                Please check all required fields and ensure your message is under 500 characters.
              </div>
            )}

            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-slate-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                <span className="text-red-800">*</span> Required fields
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-red-800 hover:bg-red-900 text-white px-8 py-3 rounded-md font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            For urgent matters or media inquiries:
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="mailto:director@algorithmicinitiative.org" className="text-red-800 hover:text-red-900 font-medium transition-colors cursor-pointer">
              <i className="ri-mail-line mr-2"></i>
              director@algorithmicinitiative.org
            </a>
            <a href="https://www.linkedin.com/company/algorithmic-consistency-initiative" target="_blank" rel="noopener noreferrer" className="text-red-800 hover:text-red-900 font-medium transition-colors cursor-pointer">
              <i className="ri-linkedin-box-fill mr-2"></i>
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
